import React, { useState, useMemo, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button, List, ListItem, ListItemIcon, Checkbox, ListItemText, TextField, Grid, Typography, Chip, Tabs, Tab } from '@mui/material';
import Close from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import NietBeschikbaarDialog from './NietBeschikbaarDialog';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Tooltip from '@mui/material/Tooltip';
import CopyMaterialsDialog from './CopyMaterialsDialog';

const CATEGORIES = [
    'Cameras', 'Drones', 'Lens', 'Monitors', 'Statief', 'Geluid', 'Grip', 'Licht',
    'Camera Accessoires', 'Special effects', 'Studio equipment', 'Vervoer en karren'
];

const SimpleMaterialWizard = ({ 
    openWizard,
    handleCloseWizard,
    materialen,
    selectedMaterials,
    setSelectedMaterials,
    onSave,
    currentReservation,
    geselecteerdeDraaidag,
    reservationsData = {},
    reservations = [],
    initialCategory = null,
    materiaalsets = []
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedUnavailableMaterial, setSelectedUnavailableMaterial] = useState(null);
    const [showUnavailableDialog, setShowUnavailableDialog] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedSet, setSelectedSet] = useState(null);
    const [sortMethod, setSortMethod] = useState('reservations'); // 'name' of 'reservations'
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    
    // Update selected category when dialog opens or initialCategory changes
    React.useEffect(() => {
        if (openWizard) {
            setSelectedCategory(initialCategory);
            setSearchTerm(''); // Reset search when opening
        }
    }, [openWizard, initialCategory]);
    
    // Check if material is available
    const isMaterialAvailable = useCallback((material) => {
        if (!geselecteerdeDraaidag) return true;
        
        // If material is already in current reservation, it's available for selection/deselection
        if (currentReservation && selectedMaterials.some(m => m.id === material.id)) {
            return true;
        }
        
        const reservations = reservationsData[material.id] || [];
        const draaidagStart = new Date(geselecteerdeDraaidag.fields.StartDatum || geselecteerdeDraaidag.fields.Start);
        const draaidagEnd = new Date(geselecteerdeDraaidag.fields.EindDatum || geselecteerdeDraaidag.fields.Eind);
        
        // Check for overlapping reservations (excluding current reservation)
        return !reservations.some(reservation => {
            // Skip if it's the current reservation
            if (reservation.reservationId === currentReservation?.id) {
                return false;
            }
            
            const resStart = new Date(reservation.startDate);
            const resEnd = new Date(reservation.endDate);
            
            // Check if dates overlap
            return (resStart <= draaidagEnd && resEnd >= draaidagStart);
        });
    }, [reservationsData, geselecteerdeDraaidag, currentReservation, selectedMaterials]);
    
    const filteredMaterials = useMemo(() => {
        let filtered = materialen || [];
        
        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(m => 
                m.fields.ID?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        if (activeTab === 0 && selectedCategory !== null) {
            filtered = filtered.filter(m => m.fields.Categorie === CATEGORIES[selectedCategory]);
        } else if (activeTab === 1 && selectedSet) {
            // Zoek de geselecteerde set in materiaalsets
            const setObj = materiaalsets.find(set => set.fields.ProjectType === selectedSet);
            // Haal de lijst met materiaal-ID's uit MaterialenFromMaterialenType
            const setMaterialIds = setObj?.fields.MaterialenFromMaterialenType || [];
            // Filter materialen op basis van materiaal-ID
            filtered = filtered.filter(m => setMaterialIds.includes(m.id));
        }
        
        // Sort by most used (aantalReserveringen)
        filtered.sort((a, b) => {
            const resA = a.fields.aantalReserveringen || 0;
            const resB = b.fields.aantalReserveringen || 0;
            return resB - resA; // Sort descending by usage
        });
        
        return filtered;
    }, [materialen, searchTerm, activeTab, selectedCategory, selectedSet, materiaalsets]);
    
    const selectedIds = useMemo(() => 
        new Set(selectedMaterials.map(m => m.id)), 
        [selectedMaterials]
    );
    
    const handleToggleMaterial = useCallback((material) => {
        if (selectedIds.has(material.id)) {
            setSelectedMaterials(prev => prev.filter(m => m.id !== material.id));
        } else {
            setSelectedMaterials(prev => [...prev, material]);
        }
    }, [selectedIds, setSelectedMaterials]);
    
    const handleSave = () => {
        if (onSave) {
            // Gebruik de draaidag-data voor de datums
            const startDate = geselecteerdeDraaidag?.fields?.Start || geselecteerdeDraaidag?.fields?.StartDatum;
            const endDate = geselecteerdeDraaidag?.fields?.Eind || geselecteerdeDraaidag?.fields?.EindDatum;
            onSave(selectedMaterials, startDate, endDate);
        }
    };
    
    // Select all/deselect all handlers
    const handleSelectAll = () => {
        setSelectedMaterials(prev => {
            const selectedIdsSet = new Set(prev.map(m => m.id));
            const newMaterials = filteredMaterials.filter(m => !selectedIdsSet.has(m.id));
            return [...prev, ...newMaterials];
        });
    };
    const handleDeselectAll = () => {
        setSelectedMaterials(prev => prev.filter(m => !filteredMaterials.some(fm => fm.id === m.id)));
    };

    // Sorteer filteredMaterials op basis van sortMethod
    const sortedMaterials = useMemo(() => {
        const arr = [...filteredMaterials];
        if (sortMethod === 'name') {
            arr.sort((a, b) => {
                const nameA = (a.fields.ID || '').toLowerCase();
                const nameB = (b.fields.ID || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
        } else {
            arr.sort((a, b) => {
                const resA = a.fields.aantalReserveringen || 0;
                const resB = b.fields.aantalReserveringen || 0;
                return resB - resA;
            });
        }
        return arr;
    }, [filteredMaterials, sortMethod]);
    
    // Groepeer materialen per categorie wanneer een materiaalset is geselecteerd
    const categorizedMaterials = useMemo(() => {
        if (activeTab !== 1 || !selectedSet) {
            return null; // Geen categorisering nodig
        }

        return sortedMaterials.reduce((acc, material) => {
            const category = material.fields.Categorie || 'Overig';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(material);
            return acc;
        }, {});
    }, [sortedMaterials, activeTab, selectedSet]);
    
    // Handler voor kopiëren
    const handleCopyMaterials = (materials) => {
        // Voeg alleen materialen toe die nog niet geselecteerd zijn
        const existingIds = new Set(selectedMaterials.map(m => m.id));
        const newMaterials = materials.filter(m => !existingIds.has(m.id));
        setSelectedMaterials(prev => [...prev, ...newMaterials]);
    };
    
    if (!openWizard) return null;
    
    return (
        <Dialog 
            open={openWizard} 
            onClose={handleCloseWizard} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { maxHeight: '90vh' }
            }}
        >
            <DialogTitle>
                {currentReservation ? 'Wijzig Reservering' : 'Nieuwe Reservering'}
                <IconButton
                    onClick={handleCloseWizard}
                    style={{ position: 'absolute', right: '8px', top: '8px' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent style={{ paddingBottom: 0 }}>
                {!currentReservation && (
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setCopyDialogOpen(true)}
                        style={{ marginBottom: 12 }}
                    >
                        Kopieer materialen van andere draaidag
                    </Button>
                )}
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} centered>
                    <Tab label="Categorieën" />
                    <Tab label="Materiaalsets" />
                </Tabs>
                {/* Categorieën knoppen */}
                {activeTab === 0 && (
                    <Grid container spacing={1} style={{ marginTop: '10px', marginBottom: '10px' }}>
                        <Grid item>
                            <Button 
                                variant={selectedCategory === null ? "contained" : "outlined"}
                                onClick={() => setSelectedCategory(null)}
                                size="small"
                            >
                                Alle
                            </Button>
                        </Grid>
                        {CATEGORIES.map((category, index) => (
                            <Grid item key={category}>
                                <Button
                                    variant={selectedCategory === index ? "contained" : "outlined"}
                                    onClick={() => setSelectedCategory(index)}
                                    size="small"
                                >
                                    {category}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                )}
                {/* Materiaalsets knoppen */}
                {activeTab === 1 && (
                    <Grid container spacing={1} style={{ marginTop: '10px', marginBottom: '10px' }}>
                        {materiaalsets.map(set => (
                            <Grid item key={set.id}>
                                <Button
                                    variant={selectedSet === set.fields.ProjectType ? "contained" : "outlined"}
                                    onClick={() => setSelectedSet(set.fields.ProjectType)}
                                    size="small"
                                >
                                    {set.fields.ProjectType}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                )}
                {/* Compacte knoppenbalk direct onder categorieën */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 0 4px 0' }}>
                    <Tooltip title="Sorteer op naam">
                        <Button
                            variant={sortMethod === 'name' ? 'contained' : 'text'}
                            size="small"
                            onClick={() => setSortMethod('name')}
                            sx={{ minWidth: 0, padding: '1px 4px', fontSize: '0.7rem', borderRadius: 8, boxShadow: 'none' }}
                        >
                            <ArrowDownwardIcon style={{ fontSize: 14 }} />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Sorteer op meest gebruikt">
                        <Button
                            variant={sortMethod === 'reservations' ? 'contained' : 'text'}
                            size="small"
                            onClick={() => setSortMethod('reservations')}
                            sx={{ minWidth: 0, padding: '1px 4px', fontSize: '0.7rem', borderRadius: 8, boxShadow: 'none', marginLeft: 2 }}
                        >
                            <ArrowDownwardIcon style={{ fontSize: 14 }} />
                        </Button>
                    </Tooltip>
                    <Button
                        size="small"
                        onClick={handleSelectAll}
                        sx={{ minWidth: 0, padding: '1px 8px', fontSize: '0.7rem', borderRadius: 8, marginLeft: 8 }}
                    >
                        Alles
                    </Button>
                    <Button
                        size="small"
                        onClick={handleDeselectAll}
                        sx={{ minWidth: 0, padding: '1px 8px', fontSize: '0.7rem', borderRadius: 8, marginLeft: 2 }}
                    >
                        Geen
                    </Button>
                </div>
                
                {/* Search */}
                <TextField
                    fullWidth
                    label="Zoek materiaal"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    margin="normal"
                    InputProps={{
                        startAdornment: <SearchIcon />
                    }}
                />
                
                {/* Materials list */}
                <div style={{ marginTop: '10px' }}>
                    <List dense>
                        {categorizedMaterials ? (
                            // Toon gegroepeerde materialen per categorie
                            Object.entries(categorizedMaterials).map(([category, materials]) => (
                                <React.Fragment key={category}>
                                    <Typography variant="h6" sx={{ mt: 2, ml: 2, color: 'primary.main', fontWeight: 'bold' }}>
                                        {category}
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {materials.map((material) => {
                                            const isSelected = selectedIds.has(material.id);
                                            const available = isMaterialAvailable(material);

                                            return (
                                                <Grid item xs={12} sm={4} key={material.id}>
                                                    <ListItem
                                                        button={available}
                                                        dense
                                                        onClick={() => {
                                                            if (available) {
                                                                handleToggleMaterial(material);
                                                            }
                                                        }}
                                                        style={{ 
                                                            backgroundColor: isSelected ? '#e3f2fd' : !available ? '#f5f5f5' : 'transparent',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '4px',
                                                            paddingTop: 2,
                                                            paddingBottom: 2,
                                                            opacity: available ? 1 : 0.6,
                                                            cursor: available ? 'pointer' : 'default',
                                                            pointerEvents: available ? 'auto' : 'none',
                                                        }}
                                                    >
                                                        {available && (
                                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                                <Checkbox
                                                                    edge="start"
                                                                    checked={isSelected}
                                                                    tabIndex={-1}
                                                                    disableRipple
                                                                />
                                                            </ListItemIcon>
                                                        )}
                                                        <ListItemText
                                                            sx={{ overflow: 'hidden' }}
                                                            primary={
                                                                <Typography variant="body2" noWrap>
                                                                    {material.fields.ID}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                <Typography variant="caption" color="textSecondary" noWrap>
                                                                    {material.fields.Omschrijving}
                                                                </Typography>
                                                            }
                                                        />
                                                        {!available && (
                                                            <Tooltip title="Bekijk reserveringen voor dit materiaal">
                                                                <Chip 
                                                                    label="Niet beschikbaar"
                                                                    size="small"
                                                                    color="error"
                                                                    style={{ marginLeft: 8, cursor: 'pointer', pointerEvents: 'auto' }}
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        setSelectedUnavailableMaterial(material);
                                                                        setShowUnavailableDialog(true);
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </ListItem>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </React.Fragment>
                            ))
                        ) : (
                            // Toon normale platte lijst
                            <Grid container spacing={1}>
                                {sortedMaterials.map((material) => {
                                    const isSelected = selectedIds.has(material.id);
                                    const available = isMaterialAvailable(material);

                                    return (
                                        <Grid item xs={12} sm={4} key={material.id}>
                                            <ListItem
                                                button={available}
                                                dense
                                                onClick={() => {
                                                    if (available) {
                                                        handleToggleMaterial(material);
                                                    }
                                                }}
                                                style={{ 
                                                    backgroundColor: isSelected ? '#e3f2fd' : !available ? '#f5f5f5' : 'transparent',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    paddingTop: 2,
                                                    paddingBottom: 2,
                                                    opacity: available ? 1 : 0.6,
                                                    cursor: available ? 'pointer' : 'default',
                                                    pointerEvents: available ? 'auto' : 'none',
                                                }}
                                            >
                                                {available && (
                                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                                        <Checkbox
                                                            edge="start"
                                                            checked={isSelected}
                                                            tabIndex={-1}
                                                            disableRipple
                                                        />
                                                    </ListItemIcon>
                                                )}
                                                <ListItemText
                                                    sx={{ overflow: 'hidden' }}
                                                    primary={
                                                        <Typography variant="body2" noWrap>
                                                            {material.fields.ID}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" color="textSecondary" noWrap>
                                                            {material.fields.Omschrijving}
                                                        </Typography>
                                                    }
                                                />
                                                {!available && (
                                                    <Tooltip title="Bekijk reserveringen voor dit materiaal">
                                                        <Chip 
                                                            label="Niet beschikbaar"
                                                            size="small"
                                                            color="error"
                                                            style={{ marginLeft: 8, cursor: 'pointer', pointerEvents: 'auto' }}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setSelectedUnavailableMaterial(material);
                                                                setShowUnavailableDialog(true);
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </ListItem>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </List>
                </div>
            </DialogContent>
            
            <DialogActions>
                <Typography variant="body2" style={{ marginRight: 'auto', marginLeft: '16px' }}>
                    {selectedMaterials.length} materialen geselecteerd
                </Typography>
                <Button onClick={handleCloseWizard}>
                    Annuleren
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                >
                    Opslaan
                </Button>
            </DialogActions>
            
            <NietBeschikbaarDialog
                open={showUnavailableDialog}
                onClose={() => setShowUnavailableDialog(false)}
                reservations={selectedUnavailableMaterial ? 
                    (reservationsData[selectedUnavailableMaterial.id] || [])
                        .filter(res => res.reservationId !== currentReservation?.id) // Filter out current reservation
                        .map(reservation => ({
                            draaidagNaam: reservation.draaidagNaam,
                            startDatum: new Date(reservation.startDate),
                            eindDatum: new Date(reservation.endDate)
                        })) : []
                }
            />
            <CopyMaterialsDialog
                open={copyDialogOpen}
                onClose={() => setCopyDialogOpen(false)}
                currentDraaidag={geselecteerdeDraaidag}
                onCopy={handleCopyMaterials}
                reservations={reservations}
            />
        </Dialog>
    );
};

export default SimpleMaterialWizard; 